/**
 * "Feedback" bij een mail in Correspondentie (20 okt 2026): klantfeedback als punten bij één
 * v2/DEF-taak zetten, zodat de editor ze in Dagstart kan afvinken.
 *
 * Drie stappen: 1. tekst kiezen (alleen die gaat naar de AI) → 2. punten nalopen + taak kiezen →
 * 3. controleren wat team-breed zichtbaar wordt → opslaan met ongedaan maken.
 * Opgeslagen worden alleen de punten en een label (onderwerp + datum), geen mailtekst of adres.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  Radio, TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

import { meldFout } from '../../utils/apiFout';
import {
  bronVanMail, formatTijd, heeftBeeldInMail, kandidaatTaken, leesFeedbackPunten, ontleedTekst, slaFeedbackOp,
  tekstVoorAi,
} from '../../utils/feedbackPunten';

const T = {
  primary: '#526075', text: '#2d3335', secondary: '#556071', outline: '#767c7e',
  surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb', font: '"Inter", system-ui, sans-serif',
};
const STAPPEN = ['Tekst', 'Punten en taak', 'Controleren'];

const datumKort = (d) => {
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
};

function Stappen({ stap }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
      {STAPPEN.map((naam, i) => (
        <Typography key={naam} sx={{ fontFamily: T.font, fontSize: '0.75rem', fontWeight: i === stap ? 700 : 500,
          color: i === stap ? T.text : T.outline }}>
          {i + 1}. {naam}
        </Typography>
      ))}
    </Box>
  );
}

export default function FeedbackUitMailDialog({ open, onClose, mail, body, projectNumber, tasks, deliverables }) {
  const [stap, setStap] = useState(0);
  const [metEerder, setMetEerder] = useState(false);
  const [tekst, setTekst] = useState('');
  const [punten, setPunten] = useState([]);
  const [taakId, setTaakId] = useState('');
  const [bestaand, setBestaand] = useState(null); // import van deze mail bij de gekozen taak
  const [bezig, setBezig] = useState(false);
  const [melding, setMelding] = useState('');

  const groepen = useMemo(() => kandidaatTaken(tasks, deliverables), [tasks, deliverables]);
  const taken = useMemo(() => groepen.flatMap((g) => g.taken.map((t) => ({ ...t, deliverableNaam: g.deliverableNaam }))), [groepen]);
  const taak = taken.find((t) => t.id === taakId) || null;
  const bron = bronVanMail(mail);
  const zieMail = heeftBeeldInMail(mail, body);
  const label = [mail?.onderwerp || 'Mail', datumKort(mail?.datum)].filter(Boolean).join(' · ');

  // Bij openen: begin bij de eigen tekst van dit bericht.
  useEffect(() => {
    if (!open) return;
    setStap(0); setMetEerder(false); setPunten([]); setBestaand(null); setMelding('');
    setTekst(tekstVoorAi(body, { onderwerp: mail?.onderwerp }));
    setTaakId(taken.length === 1 ? taken[0].id : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mail?.id]);

  const wisselEerder = () => {
    const nieuw = !metEerder;
    setMetEerder(nieuw);
    setTekst(tekstVoorAi(body, { onderwerp: mail?.onderwerp, metEerder: nieuw }));
  };

  const maakPunten = async () => {
    setBezig(true); setMelding('');
    try {
      const d = await ontleedTekst(tekst, projectNumber);
      const lijst = (d.punten || []).map((p) => ({ ...p }));
      setPunten(lijst.length ? lijst : [{ tekst: '', seconden: null, soort: 'overig' }]);
      if (!lijst.length) setMelding('De AI vond geen concrete punten. Typ ze zelf, of ga terug en kies meer tekst.');
      else if (d.ingekort) setMelding('De tekst was erg lang; alleen het begin is gelezen.');
      setStap(1);
    } catch (e) { meldFout(e, 'Punten maken'); }
    finally { setBezig(false); }
  };

  const naarControle = async () => {
    setBezig(true); setMelding('');
    try {
      const d = await leesFeedbackPunten([taakId]);
      const imp = (d?.[taakId]?.imports || []).find((i) => i.bronBerichtId === bron.bronBerichtId) || null;
      setBestaand(imp);
      setStap(2);
    } catch (e) { meldFout(e, 'Feedbackpunten laden'); }
    finally { setBezig(false); }
  };

  const schoon = punten.map((p) => ({ ...p, tekst: String(p.tekst || '').trim() })).filter((p) => p.tekst);

  const opslaan = async () => {
    setBezig(true);
    try {
      const res = await slaFeedbackOp({
        taskId: taakId, deliverableId: taak?.deliverableId || null, projectNumber,
        ...bron, bronLabel: label, zieMail, punten: schoon, aanvullen: Boolean(bestaand),
      }, { taakNaam: taak?.naam });
      if (res.alGekoppeld) { // een collega was net eerder: zelfde mail, zelfde taak
        await naarControle();
        setMelding('Deze mail is intussen al toegevoegd aan deze taak. Opslaan vult de bestaande punten aan.');
        return;
      }
      onClose?.(res);
    } catch (e) { meldFout(e, 'Feedback opslaan'); }
    finally { setBezig(false); }
  };

  const zetPunt = (i, velden) => setPunten((lijst) => lijst.map((p, j) => (j === i ? { ...p, ...velden } : p)));

  return (
    <Dialog open={open} onClose={() => !bezig && onClose?.()} maxWidth="sm" fullWidth
      onClick={(e) => e.stopPropagation()}>
      <DialogTitle sx={{ fontFamily: T.font, fontSize: '1.05rem', fontWeight: 700, pb: 0.5 }}>Feedback uit deze mail</DialogTitle>
      <DialogContent>
        <Stappen stap={stap} />
        {melding && <Alert severity="info" sx={{ mb: 2 }}>{melding}</Alert>}

        {stap === 0 && (
          <>
            <Typography sx={{ fontFamily: T.font, fontSize: '0.8rem', color: T.secondary, mb: 1 }}>
              Alleen deze tekst gaat naar de AI. Haal weg wat er niet bij hoort.
            </Typography>
            <TextField multiline minRows={6} maxRows={16} fullWidth value={tekst} onChange={(e) => setTekst(e.target.value)}
              InputProps={{ sx: { fontSize: '0.85rem', fontFamily: T.font } }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
              <Button size="small" onClick={wisselEerder} sx={{ textTransform: 'none', color: T.outline }}>
                {metEerder ? 'Alleen dit bericht' : 'Eerdere berichten ook meenemen'}
              </Button>
              <Typography sx={{ fontFamily: T.font, fontSize: '0.72rem', color: T.outline }}>{tekst.length} tekens</Typography>
            </Box>
            {zieMail && (
              <Typography sx={{ fontFamily: T.font, fontSize: '0.78rem', color: T.secondary, mt: 1 }}>
                📎 Deze mail heeft bijlagen of afbeeldingen. Die gaan niet mee; bij de punten staat "zie mail".
              </Typography>
            )}
          </>
        )}

        {stap === 1 && (
          <>
            <Typography sx={{ fontFamily: T.font, fontSize: '0.8rem', fontWeight: 700, color: T.text, mb: 1 }}>Punten</Typography>
            {punten.map((p, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                <TextField size="small" placeholder="0:00" value={p.tijd ?? formatTijd(p.seconden)} sx={{ width: 72, flexShrink: 0 }}
                  onChange={(e) => {
                    const v = e.target.value;
                    const m = /^(\d{1,3}):(\d{2})$/.exec(v.trim());
                    zetPunt(i, { tijd: v, seconden: m ? Number(m[1]) * 60 + Number(m[2]) : (v.trim() ? p.seconden : null) });
                  }}
                  InputProps={{ sx: { fontSize: '0.8rem' } }} inputProps={{ 'aria-label': 'Tijdcode' }} />
                <TextField size="small" fullWidth multiline maxRows={4} value={p.tekst} placeholder="Wat moet er anders?"
                  onChange={(e) => zetPunt(i, { tekst: e.target.value })} InputProps={{ sx: { fontSize: '0.85rem' } }} />
                <Tooltip title="Punt weghalen" arrow>
                  <IconButton size="small" onClick={() => setPunten((l) => l.filter((_, j) => j !== i))}><DeleteOutlineIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={() => setPunten((l) => [...l, { tekst: '', seconden: null, soort: 'overig' }])}
              sx={{ textTransform: 'none', color: T.outline, mb: 2 }}>Punt toevoegen</Button>

            <Typography sx={{ fontFamily: T.font, fontSize: '0.8rem', fontWeight: 700, color: T.text, mb: 0.5 }}>Bij welke versie?</Typography>
            {!taken.length && (
              <Typography sx={{ fontFamily: T.font, fontSize: '0.8rem', color: T.secondary }}>
                Dit project heeft nog geen v2- of DEF-taak. Maak die eerst aan bij Taken.
              </Typography>
            )}
            {groepen.map((g) => (
              <Box key={g.deliverableId || 'zonder'} sx={{ mb: 1 }}>
                <Typography sx={{ fontFamily: T.font, fontSize: '0.72rem', color: T.outline, mt: 1 }}>{g.deliverableNaam}</Typography>
                {g.taken.map((t) => (
                  <Box key={t.id} onClick={() => setTaakId(t.id)} role="radio" aria-checked={taakId === t.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, borderRadius: 1, cursor: 'pointer',
                      bgcolor: taakId === t.id ? T.surfaceLow : 'transparent', '&:hover': { bgcolor: T.surfaceLow } }}>
                    <Radio size="small" checked={taakId === t.id} sx={{ p: 0.5 }} inputProps={{ 'aria-label': t.naam }} />
                    <Typography sx={{ fontFamily: T.font, fontSize: '0.9rem', fontWeight: 600, color: t.afgerond ? T.outline : T.text }}>{t.naam}</Typography>
                    <Typography sx={{ fontFamily: T.font, fontSize: '0.75rem', color: T.outline }}>
                      {[t.wie, t.start ? datumKort(t.start) : '', t.afgerond ? 'afgerond' : ''].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </>
        )}

        {stap === 2 && taak && (
          <>
            <Typography sx={{ fontFamily: T.font, fontSize: '0.8rem', color: T.secondary, mb: 1.5 }}>
              Dit ziet iedereen die het project ziet, ook in Dagstart bij de taak.
            </Typography>
            {bestaand && !melding && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Deze mail is al toegevoegd aan deze taak ({bestaand.punten.length} punten). Opslaan vult de bestaande punten aan.
              </Alert>
            )}
            {!bron.bronIdZeker && (
              <Typography sx={{ fontFamily: T.font, fontSize: '0.75rem', color: T.outline, mb: 1 }}>
                Deze mail heeft geen vast bericht-id; als een collega hem ook toevoegt, kan hij dubbel staan.
              </Typography>
            )}
            <Box sx={{ border: `1px solid ${T.surfaceHigh}`, borderRadius: 1.5, p: 1.5 }}>
              <Typography sx={{ fontFamily: T.font, fontSize: '0.72rem', color: T.outline }}>{taak.deliverableNaam}</Typography>
              <Typography sx={{ fontFamily: T.font, fontSize: '1rem', fontWeight: 700, color: T.text }}>{taak.naam}</Typography>
              <Typography sx={{ fontFamily: T.font, fontSize: '0.75rem', color: T.outline, mb: 1 }}>
                Mail · {label}{zieMail ? ' · 📎 zie mail' : ''}
              </Typography>
              {schoon.map((p, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Typography key={i} sx={{ fontFamily: T.font, fontSize: '0.85rem', color: T.text, py: 0.25 }}>
                  ☐ {p.seconden != null ? `${formatTijd(p.seconden)} · ` : ''}{p.tekst}
                </Typography>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => (stap === 0 ? onClose?.() : setStap(stap - 1))} disabled={bezig} sx={{ textTransform: 'none' }}>
          {stap === 0 ? 'Annuleren' : 'Terug'}
        </Button>
        {stap === 0 && (
          <Button variant="contained" disableElevation onClick={maakPunten} disabled={bezig || !tekst.trim()} sx={{ textTransform: 'none' }}>
            {bezig ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Maak punten'}
          </Button>
        )}
        {stap === 1 && (
          <Button variant="contained" disableElevation onClick={naarControle} disabled={bezig || !taakId || !schoon.length} sx={{ textTransform: 'none' }}>
            {bezig ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Controleren'}
          </Button>
        )}
        {stap === 2 && (
          <Button variant="contained" disableElevation onClick={opslaan} disabled={bezig || !schoon.length} sx={{ textTransform: 'none' }}>
            {bezig ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : bestaand ? 'Punten aanvullen' : 'Opslaan'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
